import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/shared/lib/api";
import { useAdvisorStore } from "@/features/auth/stores/advisorStore";
import { useBreadcrumbs } from "@/shared/components/layout/DashboardLayout";
import { breadcrumbBuilders } from "@/shared/components/ui/Breadcrumbs";
import {
  Card,
  CardContent,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "@/shared/components/ui";
import {
  LeadsFilter,
  useLeadsFilter,
  filterLeads,
} from "@/shared/components/LeadsFilter";
import {
  BulkActionsBar,
  exportLeadsToCsv,
} from "@/shared/components/BulkActionsBar";
import {
  getStatusVariant,
  getStatusLabel,
  formatCurrency,
  formatDateTime,
  getCreditTypeLabel,
  maskPhone,
} from "@/shared/lib/utils";
import { Users, Phone, CreditCard, AlertCircle, Loader2, CheckCircle, Square, CheckSquare } from "lucide-react";
import type { Lead, ReserveLeadResponse } from "@/shared/types";

export function LeadsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { advisor, updateCredits } = useAdvisorStore();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [filter, setFilter] = useLeadsFilter();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { setItems: setBreadcrumbs } = useBreadcrumbs();

  // Set breadcrumbs on mount
  useEffect(() => {
    setBreadcrumbs(breadcrumbBuilders.leads());
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["leads"],
    queryFn: () => api.getLeads(),
  });

  const reserveMutation = useMutation({
    mutationFn: (leadId: string) => api.reserveLead(leadId),
    onSuccess: (response) => {
      const data = response as ReserveLeadResponse;
      // Update credits in store
      updateCredits(data.credits.remaining);
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      // Close dialog and navigate to session
      setIsDialogOpen(false);
      navigate(`/sessions/${data.session.id}`);
    },
  });

  const allLeads = (data?.leads || []) as Lead[];

  // Apply filters and sorting
  const leads = useMemo(
    () => filterLeads(allLeads, filter),
    [allLeads, filter]
  );

  // Selection handlers
  const isAllSelected = leads.length > 0 && selectedIds.size === leads.length;

  const toggleSelection = useCallback((leadId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(leads.map(l => l.id)));
  }, [leads]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Get selected leads for bulk operations
  const selectedLeads = useMemo(
    () => leads.filter(l => selectedIds.has(l.id)),
    [leads, selectedIds]
  );

  // Export to CSV
  const handleExportCsv = useCallback(() => {
    if (selectedLeads.length > 0) {
      exportLeadsToCsv(selectedLeads);
    }
  }, [selectedLeads]);

  // Bulk reserve mutation
  const bulkReserveMutation = useMutation({
    mutationFn: async (leadIds: string[]) => {
      // Reserve leads sequentially to handle credits properly
      const results: ReserveLeadResponse[] = [];
      for (const leadId of leadIds) {
        const response = await api.reserveLead(leadId);
        results.push(response as ReserveLeadResponse);
      }
      return results;
    },
    onSuccess: (results) => {
      // Update credits from last result
      if (results.length > 0) {
        updateCredits(results[results.length - 1].credits.remaining);
      }
      // Clear selection and invalidate queries
      setSelectedIds(new Set());
      setIsBulkDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
  });

  const handleBulkReserve = () => {
    if (selectedIds.size > 0) {
      setIsBulkDialogOpen(true);
    }
  };

  const confirmBulkReserve = () => {
    bulkReserveMutation.mutate(Array.from(selectedIds));
  };

  const handleReserve = (lead: Lead) => {
    setSelectedLead(lead);
    setIsDialogOpen(true);
  };

  const confirmReserve = () => {
    if (selectedLead) {
      reserveMutation.mutate(selectedLead.id);
    }
  };

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-error-500 mx-auto mb-4" />
        <p className="text-gray-600">Nie udało się pobrać leadów</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leady</h1>
          <p className="text-gray-600 mt-1">
            Dostępne zapytania od klientów
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-warning-50 rounded-lg">
          <CreditCard className="w-5 h-5 text-warning-600" />
          <span className="font-medium text-warning-700">
            {advisor?.credits || 0} kredytów
          </span>
        </div>
      </div>

      {/* Filters */}
      <LeadsFilter
        value={filter}
        onChange={setFilter}
        totalCount={allLeads.length}
        filteredCount={leads.length}
      />

      {/* Bulk Actions Bar */}
      {leads.length > 0 && (
        <BulkActionsBar
          selectedCount={selectedIds.size}
          totalCount={leads.length}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
          onBulkReserve={handleBulkReserve}
          onExportCsv={handleExportCsv}
          isAllSelected={isAllSelected}
          availableCredits={advisor?.credits || 0}
          isReserving={bulkReserveMutation.isPending}
        />
      )}

      {/* Leads List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : leads.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            {allLeads.length === 0 ? (
              <>
                <p className="text-gray-600">Brak dostępnych leadów</p>
                <p className="text-sm text-gray-500 mt-1">
                  Sprawdź ponownie później
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-600">Brak leadów pasujących do filtrów</p>
                <p className="text-sm text-gray-500 mt-1">
                  Spróbuj zmienić kryteria wyszukiwania
                </p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leads.map((lead) => {
            const isSelected = selectedIds.has(lead.id);
            return (
            <Card
              key={lead.id}
              className={`hover:shadow-md transition-shadow ${isSelected ? "ring-2 ring-primary-500 bg-primary-50/30" : ""}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    {/* Selection checkbox */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelection(lead.id);
                      }}
                      className="mt-0.5 text-gray-400 hover:text-primary-600 transition-colors"
                      aria-label={isSelected ? "Odznacz" : "Zaznacz"}
                    >
                      {isSelected ? (
                        <CheckSquare className="w-5 h-5 text-primary-600" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                    <div>
                      <h3 className="font-semibold text-gray-900">{lead.name}</h3>
                      <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                        <Phone className="w-3.5 h-3.5" />
                        {maskPhone(lead.phone || "000000000")}
                      </p>
                    </div>
                  </div>
                  <Badge variant={getStatusVariant(lead.status)}>
                    {getStatusLabel(lead.status)}
                  </Badge>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Typ kredytu:</span>
                    <span className="font-medium text-gray-900">
                      {getCreditTypeLabel(lead.creditType)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Kwota:</span>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(lead.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Data:</span>
                    <span className="text-gray-900">
                      {formatDateTime(lead.createdAt)}
                    </span>
                  </div>
                </div>

                {lead.message && (
                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded mb-4 line-clamp-2">
                    {lead.message}
                  </p>
                )}

                <Button
                  onClick={() => handleReserve(lead)}
                  className="w-full"
                  disabled={(advisor?.credits || 0) < 1}
                >
                  Zarezerwuj (1 kredyt)
                </Button>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {/* Reserve Confirmation Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Potwierdź rezerwację</DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz zarezerwować tego leada?
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            {selectedLead && (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Klient:</span>
                  <span className="font-medium">{selectedLead.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Typ kredytu:</span>
                  <span className="font-medium">
                    {getCreditTypeLabel(selectedLead.creditType)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Kwota:</span>
                  <span className="font-medium">
                    {formatCurrency(selectedLead.amount)}
                  </span>
                </div>
                <hr />
                <div className="flex justify-between text-warning-600">
                  <span>Koszt rezerwacji:</span>
                  <span className="font-medium">1 kredyt</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Pozostanie:</span>
                  <span className="font-medium">
                    {(advisor?.credits || 0) - 1} kredytów
                  </span>
                </div>
              </div>
            )}

            {reserveMutation.error && (
              <div className="mt-4 p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                <p className="text-sm text-error-700">
                  {(reserveMutation.error as Error).message}
                </p>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsDialogOpen(false)}
              disabled={reserveMutation.isPending}
            >
              Anuluj
            </Button>
            <Button
              onClick={confirmReserve}
              isLoading={reserveMutation.isPending}
              leftIcon={<CheckCircle className="w-4 h-4" />}
            >
              Zarezerwuj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Reserve Confirmation Dialog */}
      <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Potwierdź rezerwację zbiorczą</DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz zarezerwować {selectedIds.size} leadów?
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Liczba leadów:</span>
                <span className="font-medium">{selectedIds.size}</span>
              </div>
              <hr />
              <div className="flex justify-between text-warning-600">
                <span>Koszt rezerwacji:</span>
                <span className="font-medium">
                  {selectedIds.size} kredyt{selectedIds.size === 1 ? "" : selectedIds.size < 5 ? "y" : "ów"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pozostanie:</span>
                <span className="font-medium">
                  {(advisor?.credits || 0) - selectedIds.size} kredytów
                </span>
              </div>
              {(advisor?.credits || 0) < selectedIds.size && (
                <div className="p-3 bg-error-50 border border-error-200 rounded-lg">
                  <p className="text-sm text-error-700">
                    Nie masz wystarczającej liczby kredytów do rezerwacji wszystkich leadów.
                  </p>
                </div>
              )}
            </div>

            {bulkReserveMutation.error && (
              <div className="mt-4 p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                <p className="text-sm text-error-700">
                  {(bulkReserveMutation.error as Error).message}
                </p>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsBulkDialogOpen(false)}
              disabled={bulkReserveMutation.isPending}
            >
              Anuluj
            </Button>
            <Button
              onClick={confirmBulkReserve}
              isLoading={bulkReserveMutation.isPending}
              leftIcon={<CheckCircle className="w-4 h-4" />}
              disabled={(advisor?.credits || 0) < selectedIds.size}
            >
              Zarezerwuj ({selectedIds.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
